import { useCallback, useMemo, useState } from 'react';
import { atsAnalyticsApi } from '../../services/atsAnalyticsService';
import {
  buildAnalyticsWidgetQueryFilters,
  buildAnalyticsReportFilters,
  buildCanonicalAnalyticsExportDocument,
  getCanonicalExportWidgetRequests
} from './analyticsExportDocuments';

const EXPORT_ACTION_IDS = {
  PDF: 'export_pdf',
  PPTX: 'export_pptx'
};

const LONG_RANGE_CACHE_THRESHOLD_DAYS = 365;

export const useCanonicalAnalyticsExport = ({
  screenId,
  companyUID = '',
  analyticsActorId = 'system',
  filterState = {},
  widgetModels = [],
  dashboardWidgets = {},
  extraContext = {},
  companyName = 'JOBSEEQR Employer',
  language = 'en'
}) => {
  const [exporting, setExporting] = useState(false);
  const [exportMode, setExportMode] = useState('');
  const [exportMessage, setExportMessage] = useState(null);

  const reportFilters = useMemo(() => buildAnalyticsReportFilters(filterState), [filterState]);

  const exportPage = useCallback(async (format = 'pptx') => {
    if (!companyUID || exporting) return false;

    setExporting(true);
    setExportMode(format);
    setExportMessage(null);

    try {
      const widgetRequests = getCanonicalExportWidgetRequests(screenId);
      const cachePayload = reportFilters.dateRangeDays >= LONG_RANGE_CACHE_THRESHOLD_DAYS
        ? await atsAnalyticsApi.getReportCache({
          companyUid: companyUID,
          scopeType: 'canonical_screen',
          screenId,
          dateRangeDays: reportFilters.dateRangeDays,
          jobTitles: reportFilters.jobTitles,
          jobStatuses: reportFilters.jobStatuses
        }).catch(() => null)
        : null;
      const cachedDashboardWidgets = cachePayload?.dashboardWidgets || {};
      const cachedRiskHistory = Array.isArray(cachePayload?.riskHistory) ? cachePayload.riskHistory : [];
      const [reportPayload, alertsPayload, targetsPayload] = await Promise.all([
        cachePayload?.report ? Promise.resolve(cachePayload.report) : atsAnalyticsApi.getReport({
          companyUid: companyUID,
          ...reportFilters
        }),
        atsAnalyticsApi.getAlerts(companyUID, reportFilters.dateRangeDays, analyticsActorId).catch(() => ({ alerts: [] })),
        atsAnalyticsApi.getTargets(companyUID).catch(() => ({ targets: [] }))
      ]);
      const widgetResponses = await Promise.all(
        widgetRequests
          .filter(({ widgetId }) => widgetId === 'open_roles_risk' || !cachedDashboardWidgets[widgetId])
          .map(({ screenId: widgetScreenId, widgetId }) =>
          atsAnalyticsApi
            .queryDashboardWidget(
              companyUID,
              widgetScreenId,
              widgetId,
              buildAnalyticsWidgetQueryFilters(filterState, widgetScreenId),
              '',
              { revision: `canonical-export:${screenId}:${widgetId}` }
            )
            .then((response) => [widgetId, response?.widget || null])
            .catch(() => [widgetId, null])
          )
      );
      const queriedDashboardWidgets = widgetResponses.reduce((accumulator, [widgetId, widget]) => {
        if (widget) accumulator[widgetId] = widget;
        return accumulator;
      }, {});
      const mergedDashboardWidgets = {
        ...dashboardWidgets,
        ...cachedDashboardWidgets,
        ...queriedDashboardWidgets
      };

      const document = buildCanonicalAnalyticsExportDocument({
        screenId,
        report: {
          ...reportPayload,
          alerts: alertsPayload?.alerts || [],
          targets: targetsPayload?.targets || []
        },
        widgetModels,
        dashboardWidgets: mergedDashboardWidgets,
        riskHistory: cachedRiskHistory,
        filterState,
        alerts: alertsPayload?.alerts || [],
        targets: targetsPayload?.targets || [],
        extraContext,
        companyName
      });

      const exportService = await import('../../services/atsAnalyticsExport');
      const handler = format === 'pdf'
        ? exportService.exportAtsAnalyticsDocumentPdf
        : exportService.exportAtsAnalyticsDocumentDeck;

      await handler({ document, language });
      setExportMessage({ tone: 'success', text: 'Export prepared.' });
      return true;
    } catch (error) {
      console.error('Canonical analytics export failed:', error);
      setExportMessage({ tone: 'error', text: error?.message || 'Analytics export failed.' });
      return false;
    } finally {
      setExporting(false);
      setExportMode('');
    }
  }, [
    analyticsActorId,
    companyName,
    companyUID,
    dashboardWidgets,
    exporting,
    extraContext,
    filterState,
    language,
    reportFilters,
    screenId,
    widgetModels
  ]);

  const handleExportAction = useCallback((actionId) => {
    if (actionId === EXPORT_ACTION_IDS.PDF) {
      void exportPage('pdf');
      return true;
    }
    if (actionId === EXPORT_ACTION_IDS.PPTX) {
      void exportPage('pptx');
      return true;
    }
    return false;
  }, [exportPage]);

  return {
    exporting,
    exportMode,
    exportMessage,
    handleExportAction,
    exportPage
  };
};

export default useCanonicalAnalyticsExport;
